/**
 * The API's maintenance window (the backend's src/services/maintenanceMode.ts):
 * while it's on, every API request gets a 503 whose body carries the code
 * "MAINTENANCE", also as a GraphQL error. The app shows MaintenanceNotice until
 * the API answers normally again. Any other error is still an ordinary error.
 */

export const MAINTENANCE_EVENT = 'pithplay-maintenance';

/** The text approved for the move to the new database (November 3, 2026). */
export const MAINTENANCE_MESSAGE =
  "PithPlay is moving to a new database. We'll be back in a few minutes; nothing you've saved is affected.";

type MaintenanceBody = {
  code?: unknown;
  errors?: Array<{ extensions?: { code?: unknown } } | null | undefined>;
};

/** True for the API's maintenance answer: a 503 whose body says MAINTENANCE. */
export function isMaintenanceBody(status: number, body: unknown): boolean {
  if (status !== 503 || !body || typeof body !== 'object') return false;
  const { code, errors } = body as MaintenanceBody;
  if (code === 'MAINTENANCE') return true;
  return Array.isArray(errors) && errors.some((error) => error?.extensions?.code === 'MAINTENANCE');
}

/** True for an Apollo network error that is the maintenance answer. */
export function isMaintenanceError(networkError: unknown): boolean {
  if (!networkError || typeof networkError !== 'object') return false;
  const { statusCode, result } = networkError as { statusCode?: unknown; result?: unknown };
  return typeof statusCode === 'number' && isMaintenanceBody(statusCode, result);
}

/** Tells MaintenanceNotice the API is in a maintenance window. */
export function reportMaintenance(): void {
  window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT));
}
